/**
 * <read-more-text> — collapses long rich-text content to ~N words behind a
 * Read more / Read less toggle. Progressive enhancement: if this script does
 * not run, the full content stays visible.
 *
 * Attributes:
 *   data-words       integer word budget (default 400)
 *   data-more-label  collapsed button label (default "Read more")
 *   data-less-label  expanded button label (default "Read less")
 *   data-fade        "false" disables the fade gradient (default on)
 *   data-align       "left" | "center" toggle alignment (default "left")
 */
(function () {
  if (customElements.get('read-more-text')) return;

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  function debounce(fn, wait) {
    var t;
    return function () {
      var ctx = this;
      var args = arguments;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, wait);
    };
  }

  // Count whitespace-delimited words across all descendant text nodes and
  // record the position just after the budget-th word. boundary is null when
  // the content has <= budget words.
  function measure(root, budget) {
    var walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, null);
    var total = 0;
    var boundary = null;
    var re = /\S+/g;
    var node;
    while ((node = walker.nextNode())) {
      re.lastIndex = 0;
      var m;
      while ((m = re.exec(node.nodeValue))) {
        total++;
        if (!boundary && total === budget) {
          boundary = { node: node, offset: m.index + m[0].length };
        }
      }
    }
    return { total: total, boundary: boundary };
  }

  // Height (px) from the element's top to the bottom of the line holding the
  // boundary position. Layout is unaffected by overflow clipping, so this is
  // accurate even while the element is already collapsed.
  function clipHeight(el, boundary) {
    var range = document.createRange();
    var rect;
    try {
      range.setStart(boundary.node, boundary.offset);
      range.collapse(true);
      rect = range.getBoundingClientRect();
      if (!rect.height && !rect.top) {
        range.setStart(boundary.node, 0);
        range.setEnd(boundary.node, boundary.offset);
        var rects = range.getClientRects();
        if (rects.length) rect = rects[rects.length - 1];
      }
    } catch (e) {
      return 0;
    }
    return Math.ceil(rect.bottom - el.getBoundingClientRect().top);
  }

  // Nearest non-transparent background colour, so the fade blends into the
  // section background. Reads the COMPUTED colour, avoiding the Concept
  // --color-background token flip in inverse colour scopes.
  function resolveBackground(el) {
    var node = el;
    while (node && node.nodeType === 1) {
      var bg = getComputedStyle(node).backgroundColor;
      if (bg && bg !== 'transparent' &&
          !/^rgba?\(\s*0\s*,\s*0\s*,\s*0\s*,\s*0\s*\)$/.test(bg)) {
        return bg;
      }
      node = node.parentElement;
    }
    return getComputedStyle(document.body).backgroundColor || '#ffffff';
  }

  class ReadMoreText extends HTMLElement {
    connectedCallback() {
      if (this._initialised) return;
      this._initialised = true;

      this.budget = parseInt(this.getAttribute('data-words'), 10) || 400;
      this.moreLabel = this.getAttribute('data-more-label') || 'Read more';
      this.lessLabel = this.getAttribute('data-less-label') || 'Read less';
      this.useFade = this.getAttribute('data-fade') !== 'false';
      this.align = this.getAttribute('data-align') === 'center' ? 'center' : 'left';
      this.expanded = false;

      // Wait for fonts so word -> line measurement reflects final layout.
      if (document.fonts && document.fonts.ready) {
        document.fonts.ready.then(this.init.bind(this));
      } else {
        this.init();
      }
    }

    disconnectedCallback() {
      if (this._onResize) window.removeEventListener('resize', this._onResize);
    }

    init() {
      if (!this.isConnected || this._built) return;

      var result = measure(this, this.budget);
      if (!result.boundary) return; // content fits; leave untouched

      this.classList.add('read-more-text--enhanced');
      this.collapsedHeight = clipHeight(this, result.boundary);
      if (this.collapsedHeight <= 0) {
        this.classList.remove('read-more-text--enhanced');
        return;
      }

      this._built = true;
      this.buildUI();
      this.collapse(true);

      this._onResize = debounce(this.onResize.bind(this), 150);
      window.addEventListener('resize', this._onResize);

      // Keyboard users tabbing into clipped content auto-expand so focus is
      // never stranded off-screen.
      this.addEventListener('focusin', function () {
        if (!this.expanded) this.expand();
      }.bind(this));
    }

    buildUI() {
      if (!this.id) this.id = 'rmt-' + Math.random().toString(36).slice(2, 9);

      if (this.useFade) {
        this.fade = document.createElement('span');
        this.fade.className = 'read-more-text__fade';
        this.fade.setAttribute('aria-hidden', 'true');
        this.style.setProperty('--read-more-fade-color', resolveBackground(this));
        this.appendChild(this.fade);
      }

      this.toggle = document.createElement('button');
      this.toggle.type = 'button';
      this.toggle.className =
        'read-more-text__toggle read-more-text__toggle--' + this.align;
      this.toggle.setAttribute('aria-controls', this.id);
      this.toggle.innerHTML =
        '<span class="read-more-text__label"></span>' +
        '<span class="read-more-text__chevron" aria-hidden="true"></span>';
      this.label = this.toggle.querySelector('.read-more-text__label');
      this.toggle.addEventListener('click', this.onToggleClick.bind(this));

      // Sibling after the element so the toggle is never clipped.
      this.insertAdjacentElement('afterend', this.toggle);
    }

    onToggleClick() {
      if (this.expanded) this.collapse(); else this.expand();
    }

    expand() {
      this.expanded = true;
      this.classList.add('is-expanded');
      this.toggle.setAttribute('aria-expanded', 'true');
      this.label.textContent = this.lessLabel;
      if (this.fade) this.fade.style.display = 'none';

      if (reducedMotion.matches) {
        this.style.maxHeight = 'none';
        return;
      }
      this.style.maxHeight = this.scrollHeight + 'px';
      var self = this;
      var done = function () {
        self.style.maxHeight = 'none';
        self.removeEventListener('transitionend', done);
      };
      this.addEventListener('transitionend', done);
    }

    collapse(instant) {
      this.expanded = false;
      this.classList.remove('is-expanded');
      this.toggle.setAttribute('aria-expanded', 'false');
      this.label.textContent = this.moreLabel;
      if (this.fade) this.fade.style.display = '';

      if (instant || reducedMotion.matches) {
        this.style.transition = 'none';
        this.style.maxHeight = this.collapsedHeight + 'px';
        void this.offsetHeight; // reflow
        this.style.transition = '';
        return;
      }
      // Animate down: from "none" set an explicit px first, reflow, then target.
      this.style.maxHeight = this.scrollHeight + 'px';
      void this.offsetHeight;
      this.style.maxHeight = this.collapsedHeight + 'px';
    }

    onResize() {
      if (this.expanded) return;
      var result = measure(this, this.budget);
      if (!result.boundary) return;
      var h = clipHeight(this, result.boundary);
      if (h > 0) {
        this.collapsedHeight = h;
        this.style.transition = 'none';
        this.style.maxHeight = h + 'px';
        void this.offsetHeight;
        this.style.transition = '';
      }
    }
  }

  customElements.define('read-more-text', ReadMoreText);
})();
